import React, { useState, useEffect } from 'react';
import MuiGrid from '../components/MuiGrid';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  CircularProgress,
  Card,
  CardContent,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Avatar,
  Button,
  Stack,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  SelectChangeEvent,
  Theme,
  useTheme,
  Switch,
  FormControlLabel
} from '@mui/material';
import { supabase } from '../services/supabase';
import SportsKabaddiIcon from '@mui/icons-material/SportsKabaddi';
import GroupIcon from '@mui/icons-material/Group';
import GamesIcon from '@mui/icons-material/Games';
import PieChartIcon from '@mui/icons-material/PieChart';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ViewListIcon from '@mui/icons-material/ViewList';
import InfoIcon from '@mui/icons-material/Info';
import CheckIcon from '@mui/icons-material/Check';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import GavelIcon from '@mui/icons-material/Gavel';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import StarIcon from '@mui/icons-material/Star';
import type { Game, League } from '../types/models';
import { useAdminRole } from '../contexts/AdminRoleContext';
import PointsProgressGraph from '../components/league/PointsProgressGraph';


const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

function getStyles(sessionNum: number, selectedSessions: number[], theme: Theme) {
  return {
    fontWeight:
      selectedSessions.indexOf(sessionNum) === -1
        ? theme.typography.fontWeightRegular
        : theme.typography.fontWeightMedium,
  };
}

const LeagueStats: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const { isSuperAdmin } = useAdminRole();
  const [loading, setLoading] = useState(true);
  const [league, setLeague] = useState<League | null>(null);
  const [leagueStats, setLeagueStats] = useState<any | null>(null);
  const [, setGames] = useState<Game[]>([]);
  const [topPlayers, setTopPlayers] = useState<any[]>([]);
  const [availableSessions, setAvailableSessions] = useState<number[]>([]);
  const [selectedSessions, setSelectedSessions] = useState<number[]>([]);
  const [filteredLeagueStats, setFilteredLeagueStats] = useState<any | null>(null);
  const [equalizeGames, setEqualizeGames] = useState(false);
  const [bypassHidden, setBypassHidden] = useState(false);

  useEffect(() => {
    if (id) {
      fetchLeagueData(id);
      fetchLeagueSessions(id);
    }
  }, [id]);
  
  // Add an effect that recalculates stats when session filter changes
  useEffect(() => {
    if (leagueStats) {
      if (selectedSessions.length === 0) {
        // If no sessions selected, show all stats
        setFilteredLeagueStats(leagueStats);
      } else {
        // Otherwise, we'll recalculate in fetchLeagueData based on selected sessions
        setFilteredLeagueStats(null);
        const lastFetchedLeagueId = id;
        if (lastFetchedLeagueId) {
          fetchLeagueData(lastFetchedLeagueId);
        }
      }
    }
  }, [selectedSessions]);

  // Add an effect that recalculates stats when equalize games toggle changes
  useEffect(() => {
    if (id) {
      fetchLeagueData(id);
    }
  }, [equalizeGames]);

  // Auto-enable equalize games for finished leagues
  useEffect(() => {
    if (league?.is_finished_league) {
      setEqualizeGames(true);
    }
  }, [league?.is_finished_league]);

  /**
   * Calculates total points for a player based on their game performance
   * Formula:
   * - +0.6 points for each win
   * - -0.4 points for each loss
   * - Add all bonus points from games
   * - +20 bonus points if player has played more than 20 games
   * - Adjustments based on killed first statistics
   * - For first-killed CITIZEN or SHERIFF players with accurate mafia guesses:
   *   - +0.5 points for 2 correct mafia guesses
   *   - +0.8 points for 3 correct mafia guesses (all correct)
   * - Multiply the result by 10 and add 50 for a more intuitive scale
   * - Round to 1 decimal place to prevent floating point issues
   * 
   * @param wins Number of wins
   * @param losses Number of losses (total games - wins)
   * @param bonusPoints Total bonus points accumulated
   * @param totalGames Total number of games played
   * @param killedFirstCount Number of times player was killed first
   * @param killedFirstAndLostCount Number of times player was killed first and lost while being killed first
   * @param winningMoveBonus Additional bonus from correctly identifying mafia as first kill
   * @returns The calculated total points
   */
  const calculateTotalPoints = (
    wins: number, 
    losses: number, 
    bonusPoints: number, 
    totalGames: number, 
    killedFirstCount: number = 0, 
    killedFirstAndLostCount: number = 0,
    winningMoveBonus: number = 0
  ): number => {
    let points = 0;

    // isKilledFirstCount determines killed first and lost modifier
    let killedFirstModifier = 0.2
    if( killedFirstCount > 3){
      killedFirstModifier = 0.4
    }else if( killedFirstCount > 6){
      killedFirstModifier = 0.5
    }

    points += killedFirstAndLostCount * killedFirstModifier

    // Points for wins and losses
    points += wins * 0.6;
    points -= losses * 0.4;
    
    // Add bonus points
    points += bonusPoints;
    
    // Add winning move bonus for correct mafia guesses
    points += winningMoveBonus;
    
    // Add 20 points bonus for players with 20 or more games
    if (totalGames >= 20) {
      points += 2;
    }
    
    // Apply scaling: multiply by 10 and add 50
    points = points * 10 + 50;
    
    // Round to 1 decimal place to prevent floating point issues
    return Number(points.toFixed(1));
  };

  /**
   * Calculates equalized total points by normalizing to the maximum games played
   * Only equalizes bonus points and winning move points, not killed first compensation
   *
   * @param wins Number of wins
   * @param losses Number of losses (total games - wins)
   * @param bonusPoints Total bonus points accumulated
   * @param totalGames Total number of games played
   * @param killedFirstCount Number of times player was killed first
   * @param killedFirstAndLostCount Number of times player was killed first and lost
   * @param winningMoveBonus Additional bonus from correctly identifying mafia as first kill
   * @param maxGames Maximum games played by any player in the league
   * @returns The calculated equalized total points
   */
  const calculateEqualizedTotalPoints = (
    wins: number,
    losses: number,
    bonusPoints: number,
    totalGames: number,
    killedFirstCount: number = 0,
    killedFirstAndLostCount: number = 0,
    winningMoveBonus: number = 0,
    maxGames: number
  ): number => {
    // If player has played max games or more, return normal calculation
    if (totalGames >= maxGames) {
      return calculateTotalPoints(wins, losses, bonusPoints, totalGames, killedFirstCount, killedFirstAndLostCount, winningMoveBonus);
    }

    // Require at least 10 games to qualify for equalization bonus
    if (totalGames < 10) {
      return calculateTotalPoints(wins, losses, bonusPoints, totalGames, killedFirstCount, killedFirstAndLostCount, winningMoveBonus);
    }

    // Calculate normal score first
    let points = calculateTotalPoints(wins, losses, bonusPoints, totalGames, killedFirstCount, killedFirstAndLostCount, winningMoveBonus);

    // Calculate the equalization adjustment
    const equalizablePoints = bonusPoints + winningMoveBonus;
    const averageEqualizablePerGame = totalGames > 0 ? equalizablePoints / totalGames : 0;
    const missingGames = maxGames - totalGames;
    let equalizationBonus = (averageEqualizablePerGame * missingGames) + (0.1 * missingGames);
    if(totalGames < 20) {
      equalizationBonus += 2
    }

    // Apply the equalization bonus with the same scaling as the main formula (75% effectiveness)
    const finalPoints = points + (equalizationBonus * 10 * 0.75);

    return Number(finalPoints.toFixed(1));
  };

  const fetchLeagueSessions = async (leagueId: string) => {
    try {
      const { data, error } = await supabase
        .from('games')
        .select('session_num')
        .eq('league_id', leagueId)
        .order('session_num', { ascending: false });
        
      if (error) throw error;
      
      // Extract unique session numbers
      const sessionNums = data?.map(game => game.session_num) || [];
      const uniqueSessionNums = Array.from(new Set(sessionNums));
      setAvailableSessions(uniqueSessionNums);
    } catch (error: any) {
      console.error('Error fetching session numbers:', error.message);
      setAvailableSessions([]);
    }
  };
  
  const handleSessionFilterChange = (event: SelectChangeEvent<number[]>) => {
    const {
      target: { value },
    } = event;
    
    // On autofill we get a stringified value
    const sessions = typeof value === 'string' ? value.split(',').map(v => parseInt(v, 10)) : value;
    setSelectedSessions(sessions);
  };
  
  const clearSessionFilter = () => {
    setSelectedSessions([]);
  };

  const fetchLeagueData = async (leagueId: string) => {
    console.log("Fetching league data for:", leagueId);
    if (selectedSessions.length === 0) {
      setLoading(true);
    }
    try {
      // Fetch league details
      const { data: leagueData, error: leagueError } = await supabase
        .from('leagues')
        .select(`
          *,
          club:club_id (
            id,
            name
          ),
          judge:judge_id (
            id,
            nickname,
            full_name
          )
        `)
        .eq('id', leagueId)
        .single();

      if (leagueError) throw leagueError;
      
      // Transform League data to match our type
      if (leagueData) {
        // Cast to any to access properties safely
        const leagueDataAny = leagueData as any;

        const formattedLeague = {
          id: leagueDataAny.id,
          club_id: leagueDataAny.club_id,
          judge_id: leagueDataAny.judge_id || '', // Add judge_id with default
          name: leagueDataAny.name,
          hidden: leagueDataAny.hidden || false,
          is_tournament: leagueDataAny.is_tournament || false,
          is_finished_league: leagueDataAny.is_finished_league || false,
          created_at: leagueDataAny.created_at,
          club: leagueDataAny.club ? {
            id: leagueDataAny.club.id,
            name: leagueDataAny.club.name,
            created_at: new Date().toISOString()  // Supply a default for required field
          } : undefined,
          judge: leagueDataAny.judge ? {
            id: leagueDataAny.judge.id,
            nickname: leagueDataAny.judge.nickname,
            full_name: leagueDataAny.judge.full_name,
            is_judge: true,
            created_at: new Date().toISOString()
          } : undefined
        };
        setLeague(formattedLeague);
      } else {
        setLeague(null);
      }

      // Fetch league statistics - with or without session filter
      // Define common filters for games
      let gamesFilters = supabase.from('games').select('*').eq('league_id', leagueId);

      // Apply session filter if selected
      if (selectedSessions.length > 0) {
        gamesFilters = gamesFilters.in('session_num', selectedSessions);
      }

      const { data: gamesData, error: gamesError } = await gamesFilters;

      if (gamesError) throw gamesError;

      const totalGames = gamesData?.length || 0;
      const civilianWins = gamesData?.filter(g => g.winner === 'CIVILIANS').length || 0;
      const mafiaWins = gamesData?.filter(g => g.winner === 'MAFIA').length || 0;
      const tieGames = gamesData?.filter(g => g.winner === 'TIE').length || 0;
      
      const civilianWinRate = totalGames > 0 ? ((civilianWins / totalGames) * 100).toFixed(1) : '0';
      const mafiaWinRate = totalGames > 0 ? ((mafiaWins / totalGames) * 100).toFixed(1) : '0';

      const statsObj = {
        total_games: totalGames,
        civilian_wins: civilianWins,
        mafia_wins: mafiaWins,
        tie_games: tieGames,
        civilian_win_rate: civilianWinRate,
        mafia_win_rate: mafiaWinRate
      };

      setLeagueStats(statsObj);
      setFilteredLeagueStats(statsObj);

      // Save recent games for reference
      const { data: recentGamesData, error: recentGamesError } = await supabase
        .from('games')
        .select(`
          *,
          judge:judge_id (
            id,
            nickname,
            full_name
          )
        `)
        .eq('league_id', leagueId)
        .order('occurred_at', { ascending: false })
        .limit(5);

      if (recentGamesError) throw recentGamesError;
      setGames(recentGamesData || []);

      // Get all games in this league, filtered by session if needed
      let gamesQuery = supabase.from('games').select('id').eq('league_id', leagueId);
      
      // Apply session filter if selected
      if (selectedSessions.length > 0) {
        gamesQuery = gamesQuery.in('session_num', selectedSessions);
      }
      
      const { data: leagueGames, error: leagueGamesError } = await gamesQuery;
        
      if (leagueGamesError) {
        console.error("Error fetching league games:", leagueGamesError);
        throw leagueGamesError;
      }
      
      if (!leagueGames || leagueGames.length === 0) {
        setTopPlayers([]);
        return;
      }
      
      // Extract game IDs
      const gameIds = leagueGames.map(game => game.id);
      
      // Fetch all game results for these games, including bonus points, killed first status, and winning_move
      const { data: playersData, error: playersError } = await supabase
        .from('game_results')
        .select(`
          player_id,
          game_id,
          player:player_id (
            id,
            nickname,
            full_name,
            profile_picture_url
          ),
          is_win,
          role,
          bonus_points,
          is_killed_first,
          winning_move,
          player_num
        `)
        .in('game_id', gameIds);

      if (playersError) {
        console.error("Error fetching players:", playersError);
        throw playersError;
      }

      // If no player data, set empty array and exit
      if (!playersData || playersData.length === 0) {
        setTopPlayers([]);
        return;
      }

      // Group game results by game_id to analyze correct mafia votes
      const gameResultsByGameId: {[gameId: string]: any[]} = {};
      playersData.forEach(result => {
        if (!gameResultsByGameId[result.game_id]) {
          gameResultsByGameId[result.game_id] = [];
        }
        gameResultsByGameId[result.game_id].push(result);
      });
      
      // Compile player statistics
      const playerStats: {[key: string]: any} = {};
      playersData.forEach(result => {
        if (!result.player_id || !result.player) {
          return;
        }
        
        const playerId = result.player_id;
        if (!playerStats[playerId]) {
          playerStats[playerId] = {
            player: result.player,
            wins: 0,
            losses: 0,
            games: 0,
            totalBonusPoints: 0,
            killedFirstCount: 0,
            killedFirstAndLostCount: 0,
            winningMoveBonus: 0,
            winRate: 0,
            totalPoints: 0,
            // Role-specific stats for tournament MVP calculations
            roleStats: {
              CITIZEN: { games: 0, bonusPoints: 0 },
              SHERIFF: { games: 0, bonusPoints: 0 },
              MAFIA: { games: 0, bonusPoints: 0 },
              DON: { games: 0, bonusPoints: 0 }
            }
          };
        }
        
        // Count wins and losses
        if (result.is_win) {
          playerStats[playerId].wins += 1;
        } else {
          playerStats[playerId].losses += 1;
        }
        
        // Add bonus points (ensuring they're numbers)
        const bonusPoints = Number(result.bonus_points || 0);
        playerStats[playerId].totalBonusPoints += bonusPoints;

        // Track role-specific stats
        if (result.role && playerStats[playerId].roleStats[result.role]) {
          playerStats[playerId].roleStats[result.role].games += 1;
          playerStats[playerId].roleStats[result.role].bonusPoints += bonusPoints;
        }

        // Track killed first statistics
        if (result.is_killed_first) {
          playerStats[playerId].killedFirstCount += 1;
          
          // Track killed first and lost statistics (when both conditions are true)
          if (!result.is_win) {
            playerStats[playerId].killedFirstAndLostCount += 1;
          }
          
          // Calculate winning move bonus for first-killed CITIZEN or SHERIFF
          if ((result.role === 'CITIZEN' || result.role === 'SHERIFF') && result.winning_move) {
            // Get all players in this game
            const gameResults = gameResultsByGameId[result.game_id] || [];
            
            // Parse the winning_move to get the player numbers guessed as mafia
            let guessedMafiaNumbers = [];
            try {
              // More robust parsing - handle various formats
              if (result.winning_move.includes(',')) {
                guessedMafiaNumbers = result.winning_move.split(',').map((n: string) => parseInt(n.trim(), 10));
              } else if (result.winning_move.includes(' ')) {
                guessedMafiaNumbers = result.winning_move.split(' ').map((n: string) => parseInt(n.trim(), 10));
              } else {
                // Try to extract numbers from any string
                guessedMafiaNumbers = result.winning_move.match(/\d+/g)?.map((n: string) => parseInt(n, 10)) || [];
              }
              // Find the actual mafia players in this game
              const mafiaPlayers = gameResults.filter(gr => 
                gr.role === 'MAFIA' || gr.role === 'DON'
              );
              
              console.log('Actual mafia/don players:', mafiaPlayers);
              
              // Count how many guesses were correct
              const correctGuesses = guessedMafiaNumbers.filter(playerNum => 
                mafiaPlayers.some(mp => mp.player_num === playerNum)
              ).length;
              
              console.log(`Correct guesses: ${correctGuesses} out of ${guessedMafiaNumbers.length}`);
              
              // Apply appropriate bonus
              if (correctGuesses === 2) {
                console.log(`Adding 0.5 points bonus for ${result.player.nickname}`);
                playerStats[playerId].winningMoveBonus = (playerStats[playerId].winningMoveBonus || 0) + 0.5;
              } else if (correctGuesses === 3) {
                console.log(`Adding 0.8 points bonus for ${result.player.nickname}`);
                playerStats[playerId].winningMoveBonus = (playerStats[playerId].winningMoveBonus || 0) + 0.8;
              }
            } catch (e) {
              console.error('Error processing winning move:', e);
            }
          }
        }
        
        // Count total games
        playerStats[playerId].games += 1;
      });
      
      // Find max games across all players for equalization
      const playersArray = Object.values(playerStats);
      const maxGames = Math.max(...playersArray.map((player: any) => player.games));

      // Calculate win rates and total points
      Object.values(playerStats).forEach((player: any) => {
        // Calculate win rate
        player.winRate = player.games > 0 ?
          Math.round((player.wins / player.games) * 100) : 0;

        // Calculate total points using normal or equalized formula based on toggle
        if (equalizeGames) {
          player.totalPoints = calculateEqualizedTotalPoints(
            player.wins,
            player.losses,
            player.totalBonusPoints,
            player.games,
            player.killedFirstCount,
            player.killedFirstAndLostCount,
            player.winningMoveBonus,
            maxGames
          );
          // Store max games for display purposes
          player.maxGames = maxGames;
        } else {
          player.totalPoints = calculateTotalPoints(
            player.wins,
            player.losses,
            player.totalBonusPoints,
            player.games,
            player.killedFirstCount,
            player.killedFirstAndLostCount,
            player.winningMoveBonus
          );
        }
      });

      // Convert to array
      const allPlayersList = Object.values(playerStats);
      
      // Sort by total points (primary), then by bonus points (secondary tie-breaker)
      const sortedPlayersList = [...allPlayersList].sort((a: any, b: any) => {
        const pointsDiff = b.totalPoints - a.totalPoints;
        if (pointsDiff !== 0) return pointsDiff;
        // If points are equal, sort by total bonus points (including winning move bonus)
        const aBonusTotal = (a.totalBonusPoints || 0) + (a.winningMoveBonus || 0);
        const bBonusTotal = (b.totalBonusPoints || 0) + (b.winningMoveBonus || 0);
        return bBonusTotal - aBonusTotal;
      });
      
      // Take the top 50 players
      const topPlayersList = sortedPlayersList.slice(0, 50);
      
      setTopPlayers(topPlayersList);
    } catch (error: any) {
      console.error('Error fetching league data:', error.message);
    } finally {
      setLoading(false);
    }
  };
  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {league ? (
              <>
                <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="h4" component="h1">
                      {league.name}
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                      Club: {(league.club as any)?.name || 'Unknown Club'}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={2} alignItems="center">
                    {league.hidden && bypassHidden && (
                      <Chip
                        icon={<VisibilityIcon />}
                        label="Admin View"
                        color="warning"
                        size="small"
                      />
                    )}
                    <Button
                      variant="outlined"
                      startIcon={<ArrowBackIcon />}
                      onClick={() => navigate('/leagues')}
                    >
                      All Leagues
                    </Button>
                    {(!league.hidden || bypassHidden) && (
                      <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<ViewListIcon />}
                        onClick={() => navigate(`/games?league=${league.id}`)}
                      >
                        View Games
                      </Button>
                    )}
                  </Stack>
                </Box>

                {/* Show In Progress view when league is hidden (unless superadmin bypasses) */}
                {league.hidden && !bypassHidden ? (
                  <Paper sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                      <Avatar sx={{ width: 80, height: 80, bgcolor: 'warning.main' }}>
                        <HourglassEmptyIcon sx={{ fontSize: 48 }} />
                      </Avatar>

                      <Typography variant="h4" color="warning.main" fontWeight="bold">
                        League In Progress
                      </Typography>

                      <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500 }}>
                        Results for this league are currently hidden. Check back later for the final standings!
                      </Typography>

                      <Box sx={{ display: 'flex', gap: 4, mt: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <HomeWorkIcon color="primary" />
                          <Box>
                            <Typography variant="caption" color="text.secondary">Club</Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {(league.club as any)?.name || 'Unknown Club'}
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <GavelIcon color="primary" />
                          <Box>
                            <Typography variant="caption" color="text.secondary">Judge</Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {(league.judge as any)?.nickname || 'Unknown Judge'}
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <GamesIcon color="primary" />
                          <Box>
                            <Typography variant="caption" color="text.secondary">Games Played</Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {filteredLeagueStats?.total_games || 0}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      {/* Superadmin can bypass and view results */}
                      {isSuperAdmin && (
                        <Button
                          variant="contained"
                          color="warning"
                          startIcon={<VisibilityIcon />}
                          onClick={() => setBypassHidden(true)}
                          sx={{ mt: 2 }}
                        >
                          View Results (Admin Only)
                        </Button>
                      )}
                    </Box>
                  </Paper>
                ) : (
                  <>
                {filteredLeagueStats && (
                  <MuiGrid container spacing={3} sx={{ mb: 4 }}>
                    <MuiGrid item xs={12} sm={6} md={3}>
                      <Card sx={{
                        height: '100%',
                        background: 'linear-gradient(145deg, rgba(92, 107, 192, 0.15), rgba(92, 107, 192, 0.3))',
                        color: 'white',
                        border: '1px solid rgba(92, 107, 192, 0.2)'
                      }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                              <GamesIcon />
                            </Avatar>
                            <Box>
                              <Typography variant="h4" component="div">
                                {filteredLeagueStats.total_games}
                              </Typography>
                              <Typography variant="body2">
                                {selectedSessions.length > 0 
                                  ? `Games in ${selectedSessions.length === 1 ? 'Session' : 'Sessions'} ${selectedSessions.join(', ')}`
                                  : 'Total Games'}
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </MuiGrid>
                    
                    <MuiGrid item xs={12} sm={6} md={3}>
                      <Card sx={{ 
                        height: '100%', 
                        background: 'linear-gradient(145deg, rgba(76, 175, 80, 0.15), rgba(76, 175, 80, 0.3))',
                        color: 'white',
                        border: '1px solid rgba(76, 175, 80, 0.2)'
                      }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                              <GroupIcon />
                            </Avatar>
                            <Box>
                              <Typography variant="h4" component="div">
                                {filteredLeagueStats.civilian_wins}
                              </Typography>
                              <Typography variant="body2">
                                Civilian Wins ({filteredLeagueStats.civilian_win_rate}%)
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </MuiGrid>
                    
                    <MuiGrid item xs={12} sm={6} md={3}>
                      <Card sx={{ 
                        height: '100%', 
                        background: 'linear-gradient(145deg, rgba(244, 67, 54, 0.15), rgba(244, 67, 54, 0.3))',
                        color: 'white',
                        border: '1px solid rgba(244, 67, 54, 0.2)'
                      }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar sx={{ bgcolor: 'error.main', mr: 2 }}>
                              <SportsKabaddiIcon />
                            </Avatar>
                            <Box>
                              <Typography variant="h4" component="div">
                                {filteredLeagueStats.mafia_wins}
                              </Typography>
                              <Typography variant="body2">
                                Mafia Wins ({filteredLeagueStats.mafia_win_rate}%)
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </MuiGrid>
                    
                    <MuiGrid item xs={12} sm={6} md={3}>
                      <Card sx={{ 
                        height: '100%', 
                        background: 'linear-gradient(145deg, rgba(158, 158, 158, 0.15), rgba(158, 158, 158, 0.3))',
                        color: 'white',
                        border: '1px solid rgba(158, 158, 158, 0.2)'
                      }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar sx={{ bgcolor: 'grey.500', mr: 2 }}>
                              <PieChartIcon />
                            </Avatar>
                            <Box>
                              <Typography variant="h4" component="div">
                                {filteredLeagueStats.tie_games}
                              </Typography>
                              <Typography variant="body2">
                                Tie Games
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </MuiGrid>
                  </MuiGrid>
                )}

                {/* Tournament/Finished League Results Section */}
                {(league.is_tournament || league.is_finished_league) && topPlayers.length > 0 && (
                  <Paper
                    sx={{
                      p: 4,
                      mb: 4,
                      borderRadius: 3,
                      background: 'linear-gradient(145deg, rgba(255, 215, 0, 0.1), rgba(255, 152, 0, 0.15))',
                      border: '2px solid rgba(255, 215, 0, 0.3)'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                      <EmojiEventsIcon sx={{ fontSize: 40, color: '#FFD700' }} />
                      <Typography variant="h4" fontWeight="bold" sx={{ color: '#FFD700' }}>
                        {league.is_finished_league ? 'League Results' : 'Tournament Results'}
                      </Typography>
                    </Box>

                    {/* Podium - Top 3 Players */}
                    <Box sx={{ mb: 5 }}>
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'flex-end',
                        gap: { xs: 1, sm: 3 },
                        flexWrap: 'wrap'
                      }}>
                        {/* 2nd Place */}
                        {topPlayers[1] && (
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              order: { xs: 2, sm: 1 }
                            }}
                            onClick={() => navigate(`/player/${topPlayers[1].player.id}`)}
                          >
                            <Avatar
                              src={topPlayers[1].player.profile_picture_url}
                              sx={{
                                width: 70,
                                height: 70,
                                border: '4px solid #C0C0C0',
                                mb: 1,
                                cursor: 'pointer',
                                '&:hover': { transform: 'scale(1.05)' },
                                transition: 'transform 0.2s',
                                fontSize: '1.5rem'
                              }}
                            >
                              {topPlayers[1].player.nickname?.substring(0, 2).toUpperCase()}
                            </Avatar>
                            <Box
                              sx={{
                                bgcolor: '#C0C0C0',
                                borderRadius: '8px 8px 0 0',
                                p: 2,
                                pt: 1.5,
                                minWidth: 100,
                                textAlign: 'center',
                                height: 120,
                                cursor: 'pointer'
                              }}
                            >
                              <Typography variant="h4" fontWeight="bold" sx={{ color: '#1a1a1a' }}>2</Typography>
                              <Typography variant="body2" sx={{ color: '#1a1a1a' }} noWrap>
                                {topPlayers[1].player.nickname}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#333' }}>
                                {topPlayers[1].totalPoints.toFixed(1)} pts
                              </Typography>
                            </Box>
                          </Box>
                        )}

                        {/* 1st Place */}
                        {topPlayers[0] && (
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              order: { xs: 1, sm: 2 }
                            }}
                            onClick={() => navigate(`/player/${topPlayers[0].player.id}`)}
                          >
                            <StarIcon sx={{ fontSize: 30, color: '#FFD700', mb: -1 }} />
                            <Avatar
                              src={topPlayers[0].player.profile_picture_url}
                              sx={{
                                width: 90,
                                height: 90,
                                border: '4px solid #FFD700',
                                mb: 1,
                                cursor: 'pointer',
                                '&:hover': { transform: 'scale(1.05)' },
                                transition: 'transform 0.2s',
                                fontSize: '2rem'
                              }}
                            >
                              {topPlayers[0].player.nickname?.substring(0, 2).toUpperCase()}
                            </Avatar>
                            <Box
                              sx={{
                                bgcolor: '#FFD700',
                                borderRadius: '8px 8px 0 0',
                                p: 2,
                                pt: 1.5,
                                minWidth: 120,
                                textAlign: 'center',
                                height: 150,
                                cursor: 'pointer'
                              }}
                            >
                              <Typography variant="h3" fontWeight="bold" sx={{ color: '#1a1a1a' }}>1</Typography>
                              <Typography variant="body1" fontWeight="bold" sx={{ color: '#1a1a1a' }} noWrap>
                                {topPlayers[0].player.nickname}
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#333' }}>
                                {topPlayers[0].totalPoints.toFixed(1)} pts
                              </Typography>
                            </Box>
                          </Box>
                        )}

                        {/* 3rd Place */}
                        {topPlayers[2] && (
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              order: 3
                            }}
                            onClick={() => navigate(`/player/${topPlayers[2].player.id}`)}
                          >
                            <Avatar
                              src={topPlayers[2].player.profile_picture_url}
                              sx={{
                                width: 60,
                                height: 60,
                                border: '4px solid #CD7F32',
                                mb: 1,
                                cursor: 'pointer',
                                '&:hover': { transform: 'scale(1.05)' },
                                transition: 'transform 0.2s',
                                fontSize: '1.3rem'
                              }}
                            >
                              {topPlayers[2].player.nickname?.substring(0, 2).toUpperCase()}
                            </Avatar>
                            <Box
                              sx={{
                                bgcolor: '#CD7F32',
                                borderRadius: '8px 8px 0 0',
                                p: 2,
                                pt: 1,
                                minWidth: 90,
                                textAlign: 'center',
                                height: 100,
                                cursor: 'pointer'
                              }}
                            >
                              <Typography variant="h5" fontWeight="bold" sx={{ color: '#1a1a1a' }}>3</Typography>
                              <Typography variant="body2" sx={{ color: '#1a1a1a' }} noWrap>
                                {topPlayers[2].player.nickname}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#333' }}>
                                {topPlayers[2].totalPoints.toFixed(1)} pts
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Box>

                    {/* MVP Section */}
                    <MuiGrid container spacing={3} justifyContent="center">
                      {/* Overall MVP */}
                      <MuiGrid item xs={12} md={6}>
                        <Paper sx={{ p: 3, borderRadius: 2, bgcolor: 'rgba(255, 215, 0, 0.1)' }}>
                          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <StarIcon sx={{ color: '#FFD700' }} /> MVP (Most Bonus Points)
                          </Typography>
                          {(() => {
                            const mvp = [...topPlayers].sort((a, b) =>
                              (b.totalBonusPoints + b.winningMoveBonus) - (a.totalBonusPoints + a.winningMoveBonus)
                            )[0];
                            return mvp ? (
                              <Box
                                sx={{ display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer' }}
                                onClick={() => navigate(`/player/${mvp.player.id}`)}
                              >
                                <Avatar src={mvp.player.profile_picture_url} sx={{ width: 50, height: 50, bgcolor: '#FFD700' }}>
                                  {mvp.player.nickname?.substring(0, 2).toUpperCase()}
                                </Avatar>
                                <Box>
                                  <Typography variant="body1" fontWeight="bold">{mvp.player.nickname}</Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    +{(mvp.totalBonusPoints + mvp.winningMoveBonus).toFixed(1)} bonus points
                                  </Typography>
                                </Box>
                              </Box>
                            ) : null;
                          })()}
                        </Paper>
                      </MuiGrid>

                      {/* Most Killed First */}
                      <MuiGrid item xs={12} md={6}>
                        <Paper sx={{ p: 3, borderRadius: 2, bgcolor: 'rgba(244, 67, 54, 0.1)' }}>
                          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span style={{ fontSize: '1.2rem' }}>🎯</span> Most Killed First
                          </Typography>
                          {(() => {
                            const mostKilled = [...topPlayers].sort((a, b) =>
                              b.killedFirstCount - a.killedFirstCount
                            )[0];
                            return mostKilled && mostKilled.killedFirstCount > 0 ? (
                              <Box
                                sx={{ display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer' }}
                                onClick={() => navigate(`/player/${mostKilled.player.id}`)}
                              >
                                <Avatar src={mostKilled.player.profile_picture_url} sx={{ width: 50, height: 50, bgcolor: 'error.main' }}>
                                  {mostKilled.player.nickname?.substring(0, 2).toUpperCase()}
                                </Avatar>
                                <Box>
                                  <Typography variant="body1" fontWeight="bold">{mostKilled.player.nickname}</Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {mostKilled.killedFirstCount} times killed first
                                  </Typography>
                                </Box>
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">No data available</Typography>
                            );
                          })()}
                        </Paper>
                      </MuiGrid>
                    </MuiGrid>

                    {/* MVP per Role */}
                    <MuiGrid container spacing={3} sx={{ mt: 3 }} justifyContent="center">
                      {['CITIZEN', 'SHERIFF', 'MAFIA', 'DON'].map((role) => {
                        const roleColors: {[key: string]: string} = {
                          CITIZEN: '#e57373',
                          SHERIFF: '#c62828',
                          MAFIA: '#424242',
                          DON: '#212121'
                        };
                        const roleNames: {[key: string]: string} = {
                          CITIZEN: 'Citizen',
                          SHERIFF: 'Sheriff',
                          MAFIA: 'Mafia',
                          DON: 'Don'
                        };
                        const topByRole = [...topPlayers]
                          .filter(p => p.roleStats && p.roleStats[role] && p.roleStats[role].games > 0)
                          .sort((a, b) => b.roleStats[role].bonusPoints - a.roleStats[role].bonusPoints)
                          .slice(0, 3);

                        return (
                          <MuiGrid item xs={6} md={3} key={role}>
                            <Paper sx={{ p: 2, borderRadius: 2, bgcolor: `${roleColors[role]}15` }}>
                              <Box
                                sx={{
                                  bgcolor: roleColors[role],
                                  borderRadius: 1,
                                  px: 1.5,
                                  py: 0.5,
                                  mb: 1.5,
                                  display: 'inline-block'
                                }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  fontWeight="bold"
                                  sx={{ color: role === 'MAFIA' || role === 'DON' ? '#fff' : '#1a1a1a' }}
                                >
                                  Best {roleNames[role]}
                                </Typography>
                              </Box>
                              {topByRole.length > 0 ? (
                                <Stack spacing={1}>
                                  {topByRole.map((player, idx) => (
                                    <Box
                                      key={player.player.id}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        cursor: 'pointer',
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                                        borderRadius: 1,
                                        p: 0.5
                                      }}
                                      onClick={() => navigate(`/player/${player.player.id}`)}
                                    >
                                      <Typography variant="body2" sx={{ color: roleColors[role], fontWeight: 'bold', minWidth: 20 }}>
                                        {idx + 1}.
                                      </Typography>
                                      <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                                        {player.player.nickname}
                                      </Typography>
                                      <Chip
                                        label={`+${player.roleStats[role].bonusPoints.toFixed(1)}`}
                                        size="small"
                                        sx={{ bgcolor: roleColors[role], color: 'white', fontSize: '0.7rem' }}
                                      />
                                    </Box>
                                  ))}
                                </Stack>
                              ) : (
                                <Typography variant="body2" color="text.secondary">No data</Typography>
                              )}
                            </Paper>
                          </MuiGrid>
                        );
                      })}
                    </MuiGrid>
                  </Paper>
                )}

                {/* Top 5 over time Graph - Only for finished leagues */}
                {league.is_finished_league && topPlayers.length > 0 && (
                  <PointsProgressGraph
                    topPlayers={topPlayers.slice(0, 5)}
                    leagueId={league.id}
                    availableSessions={availableSessions}
                    equalizeGames={equalizeGames}
                  />
                )}

                {/* Filter and Scoring Options - Desktop 50/50, Mobile stacked */}
                {/* Only show at top for non-finished leagues */}
                {!league.is_finished_league && (
                <MuiGrid container spacing={3} sx={{ mb: 4 }}>
                  <MuiGrid item xs={12} md={6} sx={{ display: 'flex' }}>
                    {/* Session filter */}
                    <Paper sx={{ p: 3, borderRadius: 2, width: '100%', display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" component="h3" gutterBottom sx={{ mb: 0 }}>
                          <FilterAltIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                          Filter by Session
                        </Typography>
                        {selectedSessions.length > 0 && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<FilterAltOffIcon />}
                            onClick={clearSessionFilter}
                          >
                            Show All Sessions
                          </Button>
                        )}
                      </Box>

                      <FormControl sx={{ width: '100%', flex: 1 }}>
                        <InputLabel id="session-filter-label">Sessions</InputLabel>
                        <Select
                          labelId="session-filter-label"
                          id="session-filter"
                          multiple
                          value={selectedSessions}
                          onChange={handleSessionFilterChange}
                          input={<OutlinedInput label="Sessions" />}
                          renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map((value) => (
                                <Chip
                                  key={value}
                                  label={`Session ${value}`}
                                  icon={<CalendarMonthIcon />}
                                  size="small"
                                  color="primary"
                                />
                              ))}
                            </Box>
                          )}
                          MenuProps={MenuProps}
                        >
                          {availableSessions.map((sessionNum) => (
                            <MenuItem
                              key={sessionNum}
                              value={sessionNum}
                              style={getStyles(sessionNum, selectedSessions, theme)}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                <CalendarMonthIcon sx={{ mr: 2, color: 'primary.main' }} />
                                <Typography>Session {sessionNum}</Typography>
                                {selectedSessions.includes(sessionNum) && (
                                  <CheckIcon sx={{ ml: 'auto', color: 'primary.main' }} />
                                )}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Paper>
                  </MuiGrid>

                  <MuiGrid item xs={12} md={6} sx={{ display: 'flex' }}>
                    {/* Equalize Games Toggle */}
                    <Paper sx={{ p: 3, borderRadius: 2, width: '100%', display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" component="h3" gutterBottom sx={{ mb: 0 }}>
                          <EqualizerIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                          Scoring Options
                        </Typography>
                      </Box>

                      <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-start' }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={equalizeGames}
                              onChange={(event) => setEqualizeGames(event.target.checked)}
                              color="primary"
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography>Equalize games</Typography>
                              <Tooltip
                                title={
                                  <Box sx={{ p: 1 }}>
                                    <Typography variant="subtitle2">Equalize Games Feature:</Typography>
                                    <Typography variant="body2">• Requires at least 10 games to qualify</Typography>
                                    <Typography variant="body2">• Normalizes scores to the player with most games</Typography>
                                    <Typography variant="body2">• Adds average bonus points per game × missing games</Typography>
                                    <Typography variant="body2">• Adds 0.1 points per missing game</Typography>
                                    <Typography variant="body2">• Only equalizes bonus and winning move points</Typography>
                                    <Typography variant="body2">• Games column shows max games with asterisk (*)</Typography>
                                  </Box>
                                }
                                arrow
                                placement="top"
                              >
                                <InfoIcon sx={{ ml: 1, fontSize: 16, color: 'info.main', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          }
                        />
                      </Box>
                    </Paper>
                  </MuiGrid>
                </MuiGrid>
                )}

                <Typography variant="h5" component="h2" sx={{ mb: 3, mt: 4 }}>
                  Top Players in {league.name}
                  {selectedSessions.length > 0 && (
                    <Typography component="span" variant="subtitle1" sx={{ ml: 2, verticalAlign: 'middle' }}>
                      (Sessions: {selectedSessions.sort((a, b) => a - b).join(', ')})
                    </Typography>
                  )}
                </Typography>
                
                <Paper sx={{ width: '100%', mb: 4, borderRadius: 2, overflow: 'hidden' }}>
                  {topPlayers && topPlayers.length > 0 ? (
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell width="70px" sx={{ fontWeight: 'bold' }}>#</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Player</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                Total Points
                                <Tooltip 
                                  title={
                                    <Box sx={{ p: 1 }}>
                                      <Typography variant="subtitle2">Points Formula:</Typography>
                                      <Typography variant="body2">• +0.6 points for each win</Typography>
                                      <Typography variant="body2">• -0.4 points for each loss</Typography>
                                      <Typography variant="body2">• Add all game bonus points</Typography>
                                      <Typography variant="body2">• Killed first and lost: 0-3 times +0.2 per | 4-6 times +0.4 per | 7+ times +0.5 per</Typography>
                                      <Typography variant="body2">• Winning Move: +0.5 for 2 | +0.8 for 3</Typography>
                                      <Typography variant="body2">• Final score = result × 10 + 50</Typography>
                                      <Typography variant="body2">• +20 bonus if played over 20 games</Typography>
                                    </Box>
                                  } 
                                  arrow
                                  placement="top"
                                >
                                  <InfoIcon sx={{ ml: 0.5, fontSize: 16, color: 'info.main', cursor: 'help' }} />
                                </Tooltip>
                              </Box>
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>Bonus Points</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                              <Tooltip title="Times killed first and lost the game" arrow placement="top">
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  Killed 1st & Lost
                                  <InfoIcon sx={{ ml: 0.5, fontSize: 16, color: 'info.main', cursor: 'help' }} />
                                </Box>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>Games</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>Win %</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {topPlayers.map((playerData, index) => (
                            <TableRow 
                              key={playerData.player.id}
                              onClick={() => navigate(`/player/${playerData.player.id}`)}
                              sx={{ 
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' },
                                bgcolor: index < 3 ? `rgba(255, 193, 7, ${0.05 + (index === 0 ? 0.1 : index === 1 ? 0.07 : 0.05)})` : undefined,
                                borderLeft: index < 3 ? `4px solid ${index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : '#CD7F32'}` : undefined,
                              }}
                            >
                              <TableCell>
                                <Avatar sx={{ 
                                  bgcolor: index < 3 ? 'warning.main' : 'grey.500',
                                  boxShadow: index < 3 ? 2 : 0,
                                  width: 36,
                                  height: 36,
                                  fontSize: '0.9rem',
                                  fontWeight: 'bold'
                                }}>
                                  {index + 1}
                                </Avatar>
                              </TableCell>
                              <TableCell>
                                <Box>
                                  <Typography variant="body1" fontWeight={index < 3 ? 'bold' : 'regular'}>
                                    {playerData.player.nickname}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {playerData.player.full_name}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={playerData.totalPoints.toFixed(1)}
                                  color={index < 3 ? "warning" : "default"}
                                  variant={index < 3 ? "filled" : "outlined"}
                                  sx={{ fontWeight: 'bold' }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Typography>
                                  <Typography
                                  color={playerData.totalBonusPoints + playerData.winningMoveBonus > 0 ? "success.main" :
                                        playerData.totalBonusPoints + playerData.winningMoveBonus < 0 ? "error.main" : "text.secondary"}
                                  fontWeight={playerData.totalBonusPoints + playerData.winningMoveBonus !== 0 ? 'bold' : 'normal'}
                                  >
                                  {playerData.totalBonusPoints + playerData.winningMoveBonus > 0 ? '+' : ''}{Number((playerData.totalBonusPoints + playerData.winningMoveBonus).toFixed(1))}
                                </Typography>
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Chip 
                                  label={playerData.killedFirstAndLostCount || 0}
                                  color={playerData.killedFirstAndLostCount > 3 ? "error" : 
                                         playerData.killedFirstAndLostCount > 0 ? "warning" : "default"}
                                  variant="outlined"
                                  size="small"
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Typography>
                                  {equalizeGames && playerData.games >= 10 ? `${playerData.maxGames}*` : playerData.games}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Chip 
                                  label={`${playerData.winRate}%`}
                                  color={playerData.winRate > 50 ? "success" : "default"}
                                  variant="outlined"
                                  size="small"
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <Typography variant="h6" color="text.secondary" sx={{ textAlign: 'center' }}>
                        No player data available for this league yet
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                        After games are played and results are recorded, player rankings will appear here.
                      </Typography>
                      <Button 
                        variant="outlined"
                        size="small" 
                        onClick={() => navigate(`/games?league=${league?.id}`)}
                      >
                        View Games
                      </Button>
                    </Box>
                  )}
                </Paper>
                
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => navigate(`/games?league=${league.id}`)}
                    startIcon={<ViewListIcon />}
                    sx={{ borderRadius: 2 }}
                  >
                    View All Games in This League
                  </Button>
                </Box>

                {/* Filter and Scoring Options - At bottom for finished leagues */}
                {league.is_finished_league && (
                <MuiGrid container spacing={3} sx={{ mb: 4 }}>
                  <MuiGrid item xs={12} md={6} sx={{ display: 'flex' }}>
                    {/* Session filter */}
                    <Paper sx={{ p: 3, borderRadius: 2, width: '100%', display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" component="h3" gutterBottom sx={{ mb: 0 }}>
                          <FilterAltIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                          Filter by Session
                        </Typography>
                        {selectedSessions.length > 0 && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<FilterAltOffIcon />}
                            onClick={clearSessionFilter}
                          >
                            Show All Sessions
                          </Button>
                        )}
                      </Box>

                      <FormControl sx={{ width: '100%', flex: 1 }}>
                        <InputLabel id="session-filter-label-bottom">Sessions</InputLabel>
                        <Select
                          labelId="session-filter-label-bottom"
                          id="session-filter-bottom"
                          multiple
                          value={selectedSessions}
                          onChange={handleSessionFilterChange}
                          input={<OutlinedInput label="Sessions" />}
                          renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map((value) => (
                                <Chip
                                  key={value}
                                  label={`Session ${value}`}
                                  icon={<CalendarMonthIcon />}
                                  size="small"
                                  color="primary"
                                />
                              ))}
                            </Box>
                          )}
                          MenuProps={MenuProps}
                        >
                          {availableSessions.map((sessionNum) => (
                            <MenuItem
                              key={sessionNum}
                              value={sessionNum}
                              style={getStyles(sessionNum, selectedSessions, theme)}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                <CalendarMonthIcon sx={{ mr: 2, color: 'primary.main' }} />
                                <Typography>Session {sessionNum}</Typography>
                                {selectedSessions.includes(sessionNum) && (
                                  <CheckIcon sx={{ ml: 'auto', color: 'primary.main' }} />
                                )}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Paper>
                  </MuiGrid>

                  <MuiGrid item xs={12} md={6} sx={{ display: 'flex' }}>
                    {/* Equalize Games Toggle */}
                    <Paper sx={{ p: 3, borderRadius: 2, width: '100%', display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" component="h3" gutterBottom sx={{ mb: 0 }}>
                          <EqualizerIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                          Scoring Options
                        </Typography>
                      </Box>

                      <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-start' }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={equalizeGames}
                              onChange={(event) => setEqualizeGames(event.target.checked)}
                              color="primary"
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography>Equalize games</Typography>
                              <Tooltip
                                title={
                                  <Box sx={{ p: 1 }}>
                                    <Typography variant="subtitle2">Equalize Games Feature:</Typography>
                                    <Typography variant="body2">• Requires at least 10 games to qualify</Typography>
                                    <Typography variant="body2">• Normalizes scores to the player with most games</Typography>
                                    <Typography variant="body2">• Adds average bonus points per game × missing games</Typography>
                                    <Typography variant="body2">• Adds 0.1 points per missing game</Typography>
                                    <Typography variant="body2">• Only equalizes bonus and winning move points</Typography>
                                    <Typography variant="body2">• Games column shows max games with asterisk (*)</Typography>
                                  </Box>
                                }
                                arrow
                                placement="top"
                              >
                                <InfoIcon sx={{ ml: 1, fontSize: 16, color: 'info.main', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          }
                        />
                      </Box>
                    </Paper>
                  </MuiGrid>
                </MuiGrid>
                )}
                  </>
                )}
              </>
            ) : (
              <Typography variant="h5" color="error">
                League not found
              </Typography>
            )}
          </>
        )}
      </Box>
    </Container>
  );
};

export default LeagueStats;